import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;
const CHECK_THROTTLE_MS = 5 * 60_000;

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);

  readonly updateAvailable = signal(false);
  readonly applying = signal(false);

  private lastCheck = 0;

  init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.updateAvailable.set(true));

    // A corrupted cache cannot be repaired from inside the page; only a
    // reload against the network recovers.
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => document.location.reload());

    // A kitchen tablet parked on the preparation screen never navigates, so
    // a navigation-only trigger would never reach the screen that most needs it.
    interval(UPDATE_CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.check());

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkThrottled();
      }
    });
  }

  checkThrottled(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    const now = Date.now();
    if (now - this.lastCheck < CHECK_THROTTLE_MS) {
      return;
    }
    this.lastCheck = now;
    this.check();
  }

  async applyUpdate(): Promise<void> {
    this.applying.set(true);
    try {
      await this.swUpdate.activateUpdate();
      document.location.reload();
    } catch {
      this.applying.set(false);
    }
  }

  private check(): void {
    this.lastCheck = Date.now();
    void this.swUpdate.checkForUpdate().catch(() => undefined);
  }
}
